import { MenuItem } from '@components/menu';
import { Divider } from '@sketch-note/design-system/src/components/divider/divider.component';
import { AIBulletIcon, AIClearIcon, AIEnterIcon, EnterPromptIcon } from '@sketchnote/icons';
import { TextCycler } from '@sketchnote/shared-components';
import cn from 'classnames';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { promptType } from './createWithAi';
import { PromptsList } from './data/promptData';

const requestOptions = [
  { label: ' 📜 Prompts', active: true },
  { label: '👨🏻‍💻 Personas', active: false },
  { label: '📢 Tone', active: false },
  { label: '🗞️ Add source', active: false },
];

export const PromptInput = ({
  selectedPrompt,
  setSelectedPrompt,
  handleAiRequest,
  showLoader,
}: {
  selectedPrompt: promptType;
  setSelectedPrompt: Dispatch<SetStateAction<promptType>>;
  handleAiRequest: () => void;
  showLoader: boolean;
}) => {
  const [promptInput, setPromptInput] = useState(selectedPrompt.desc ?? '');

  useEffect(() => {
    setPromptInput(selectedPrompt?.desc);
  }, [selectedPrompt]);

  useEffect(() => {
    setSelectedPrompt({ label: '', value: '', desc: promptInput });
  }, [promptInput]);

  return (
    <div className="flex flex-col mt-10 px-12 flex-grow overflow-hidden">
      <section className="flex mb-15">
        <EnterPromptIcon fill={'#2563EB'} size={24} />
        <TextareaAutosize
          className="px-12 text-sm w-full"
          placeholder="select a prompt from the list or write your own"
          value={promptInput}
          autoFocus
          onChange={(e) => setPromptInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAiRequest();
            }
          }}
          style={{ resize: 'none' }}
        />
      </section>
      <div className="flex justify-end mb-10 items-center">
        <button
          className="flex text-xs px-6 py-2 border rounded-xl items-center hover:border-primary-500 hover:text-blue-400 hover:shadow-mdl mr-6"
          onClick={() => setSelectedPrompt({ label: '', value: '', desc: '' })}
        >
          <AIClearIcon wrapperClass={'mr-4'} />
          Clear prompt
        </button>
        <button
          className="flex text-xs px-6 py-2 border rounded-xl items-center bg-primary-500 hover:shadow-mdl text-white mr-4"
          onClick={() => {
            handleAiRequest();
          }}
        >
          <span className="pr-4">Create file</span>
          <AIEnterIcon fill={'#ffff'} />
        </button>
      </div>
      <Divider />
      {showLoader ? (
        <div className="flex flex-col pt-50">
          <iframe
            className="items-center justify-center"
            src="https://lottie.host/embed/8cddd9f3-7298-4b93-989e-971400bc048a/tsCt4v53hM.json"
          ></iframe>
          <TextCycler
            texts={[
              'Hi, I’m Sketchnote AI, here to change your life',
              'Nah, got you! But boosting your work efficiency?',
              'Now, that’s my jam. Loading up the goods!',
            ]}
            delayBetweenStrings={5000}
            className="text-gray-700 mt-12 animate-pulse text-sm items-center justify-center text-center"
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col mt-15 flex-grow overflow-auto">
            <span className="text-sm">📜 Prompts</span>
            <p className="text-xs text-gray-500">Select prompt from the list below</p>

            <ShowPropmts selectedPrompt={selectedPrompt} setSelectedPrompt={setSelectedPrompt} />
          </div>
        </>
      )}
    </div>
  );
};

const ShowPropmts = ({
  selectedPrompt,
  setSelectedPrompt,
}: {
  selectedPrompt: promptType;
  setSelectedPrompt: Dispatch<SetStateAction<promptType>>;
}): JSX.Element => {
  const handleSelectedPrompt = (item: any) => {
    setSelectedPrompt(item);
  };

  const ParsePrompt = (desc: string) => {
    const regex = /\[([^\]]+)]/g;
    const modifiedSentence = desc.replace(regex, '<span style="color: #0000FF">[$1]</span>');
    return modifiedSentence;
  };

  return (
    <div className="flex flex-col mt-10 overflow-auto flex-grow">
      {PromptsList.map((item, index) => {
        return (
          <MenuItem key={index} onClick={handleSelectedPrompt.bind(null, item)}>
            <span
              className={cn(
                { 'flex mt-6': true },
                { 'bg-blue-200': selectedPrompt.value === item.value }
              )}
            >
              <span className="inline-block">
                <AIBulletIcon size={24} fill={'#2563EB'} />
              </span>
              <p className="text-sm ml-10 font-regular">
                <div dangerouslySetInnerHTML={{ __html: ParsePrompt(item.desc) }}></div>
              </p>
            </span>
          </MenuItem>
        );
      })}
    </div>
  );
};
